// src/components/quizzes/UniversalMathBonus.js
//
// The bonus round for topics whose bonusRoundType is "timesTable:7",
// "countBy:5", "addition:10" or "subtraction:10" (BonusRouter passes mode +
// value). Runs on the shared bonus-round engine (kit/BonusShell.js): the
// BonusDiamonds look, bombs, bonus stars, clocks, combos, 🏁 End game.
//
// Contract unchanged: onBonusComplete(score) — now (score, { endedEarly }).

import React from "react";
import BonusShell from "./kit/BonusShell";

const randomBetween = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const TITLES = {
  timestable: ["Times Tables Blast!", "✖️"],
  countby: ["Count By Challenge!", "🔢"],
  addition: ["Addition Pop!", "➕"],
  subtraction: ["Subtraction Pop!", "➖"],
};

const nearby = (answer) => {
  let v = answer;
  while (v === answer) v = Math.max(0, answer + randomBetween(-10, 10));
  return v;
};

const UniversalMathBonus = ({ mode = "timesTable", value = 5, onBonusComplete, placeholderImage }) => {
  const m = String(mode).toLowerCase();
  const n = Number(value) || 1;
  const [title, emoji] = TITLES[m] || TITLES.timestable;

  // one question; count-by walks a sequence instead
  const build = (prev) => {
    if (m === "countby") return { i: prev ? prev.i : 1 };
    for (let k = 0; k < 8; k += 1) {
      let q;
      if (m === "addition") {
        const a = randomBetween(0, n);
        const b = randomBetween(0, n);
        q = { big: `${a} + ${b} = ?`, say: `${a} plus ${b}.`, answer: a + b };
      } else if (m === "subtraction") {
        const a = randomBetween(n, n + 10);
        const b = randomBetween(0, n);
        q = { big: `${a} − ${b} = ?`, say: `${a} minus ${b}.`, answer: a - b };
      } else {
        const b = randomBetween(0, 12);
        q = { big: `${n} × ${b} = ?`, say: `${n} times ${b}.`, answer: n * b };
      }
      if (!prev || q.big !== prev.big) return q;
    }
    return { big: `${n} × 1 = ?`, say: `${n} times 1.`, answer: n };
  };

  const answerOf = (s) => (m === "countby" ? n * (s.i + 1) : s.answer);
  const last = n * 12;

  return (
    <BonusShell
      onBonusComplete={onBonusComplete}
      placeholderImage={placeholderImage}
      title={title}
      emoji={emoji}
      sub={m === "countby" ? `Count by ${n}s — catch the next number, in order!` : "Solve the problem at the top — then catch the answer!"}
      intro={
        m === "countby"
          ? `Count by ${n}. The first number is done for you, so catch the next number. Each right number is worth ten points. Watch out for bombs! Press Start when you are ready.`
          : "Solve the problem at the top and catch the right answer. Each one is worth ten points. Watch out for bombs! Press Start when you are ready."
      }
      rules={[
        { icon: "✅", text: "Right answer", pts: "+10" },
        { icon: "❌", text: "Wrong answer", pts: "-5" },
      ]}
      init={build}
      prompt={(s) =>
        m === "countby"
          ? {
              big: `${Array.from({ length: s.i + 1 }, (_, k) => n * (k + 1)).slice(-4).join(", ")}, ?`,
              small: `Count by ${n} — catch the next number!`,
              say: `Count by ${n}.`,
            }
          : { big: s.big, small: "Catch the answer!", say: s.say }
      }
      spawn={(s, onScreen) => {
        const a = answerOf(s);
        return onScreen.filter((x) => x.value === a).length < 2 || Math.random() < 0.3 ? a : nearby(a);
      }}
      check={(s, v) => Number(v) === Number(answerOf(s))}
      onRight={(s) => {
        if (m === "countby") {
          if (answerOf(s) >= last) return { done: true, say: `You counted all the way to ${last}!`, flash: "🏆 You did it!" };
          return { state: { i: s.i + 1 }, clear: true, quiet: true };
        }
        return { state: build(s), clear: true };
      }}
      goodPoints={10}
      badPoints={5}
      starsAt={[100, 220]}
    />
  );
};

export default UniversalMathBonus;
